import * as React from "react";
import { nl } from "date-fns/locale";
import { CalendarDays, Clock } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/* Nederlandse datum-/tijdkiezers (besluit directie 4-11): altijd dd-mm-jjjj en 24-uurs tijd, ongeacht de browsertaal.
   Drop-in voor <input type="date|time|datetime-local|month">: value blijft ISO (JJJJ-MM-DD, UU:MM, JJJJ-MM-DDTUU:MM, JJJJ-MM)
   en onChange krijgt een event-achtig object met e.target.value. */

const pad = (n) => String(n).padStart(2, "0");
const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;
const NLD = /^(\d{1,2})[-./ ](\d{1,2})[-./ ](\d{4})$/;
export const MAANDEN = ["januari", "februari", "maart", "april", "mei", "juni", "juli", "augustus", "september", "oktober", "november", "december"];
const BOX = /^(-?m[trblxy]?-|w-|min-w-|max-w-|flex-|basis-|col-span-|row-span-|grow|shrink|self-|order-|hidden$|block$|inline|(sm|md|lg|xl|2xl):(w-|min-w-|max-w-|col-span-|flex-|basis-|hidden|block|inline|-?m[trblxy]?-))/;

function splitClass(className = "") {
  const delen = String(className || "").split(/\s+/).filter(Boolean);
  const box = delen.filter((c) => BOX.test(c));
  const veld = delen.filter((c) => !BOX.test(c));
  return { box: box.join(" "), veld: veld.join(" "), breed: box.some((c) => /(^|:)w-|(^|:)flex-1|(^|:)basis-|(^|:)col-span/.test(c)) };
}

function geldig(j, m, d) {
  const dt = new Date(Date.UTC(j, m - 1, d));
  return j > 1800 && j < 2200 && dt.getUTCFullYear() === j && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d ? `${j}-${pad(m)}-${pad(d)}` : null;
}

export const isoNaarNl = (iso) => { const m = ISO.exec(String(iso || "").slice(0, 10)); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };

export function nlNaarIso(tekst) {
  const s = String(tekst || "").trim();
  let m = ISO.exec(s);
  if (m) return geldig(+m[1], +m[2], +m[3]);
  m = NLD.exec(s);
  if (m) return geldig(+m[3], +m[2], +m[1]);
  const c = s.replace(/\D/g, "");
  return c.length === 8 && !/\D/.test(s.replace(/[-./ ]/g, "")) ? geldig(+c.slice(4), +c.slice(2, 4), +c.slice(0, 2)) : null;
}

const NL_DEEL = /^\d{1,2}([-./ ]\d{0,2}([-./ ]\d{0,4})?)?$/;
/** Invoer per toetsaanslag: dd-mm-jjjj-masker op de cijfers; volledig (ook als JJJJ-MM-DD of ddmmjjjj getypt) → altijd dd-mm-jjjj. */
export function maskeer(ruw) {
  const s = String(ruw || "");
  const c = s.replace(/\D/g, "").slice(0, 8);
  const iso = nlNaarIso(s) || (c.length === 8 ? geldig(+c.slice(0, 4), +c.slice(4, 6), +c.slice(6)) : null);
  if (iso) return { tekst: isoNaarNl(iso), iso };
  if (!s.trim() || NL_DEEL.test(s)) return { tekst: s, iso: null };
  return { tekst: c.length > 4 ? `${c.slice(0, 2)}-${c.slice(2, 4)}-${c.slice(4)}` : c.length > 2 ? `${c.slice(0, 2)}-${c.slice(2)}` : c, iso: null };
}

export function normTijd(tekst, afronden = false) {
  const s = String(tekst || "").trim();
  let h, mi;
  let m = /^(\d{1,2})[:.](\d{2})(?::\d{2})?$/.exec(s);
  if (m) [h, mi] = [+m[1], +m[2]];
  else if (/^\d{3,4}$/.test(s)) [h, mi] = [+s.slice(0, s.length - 2), +s.slice(-2)];
  else if (afronden && /^\d{1,2}$/.test(s)) [h, mi] = [+s, 0];
  else return null;
  return h < 24 && mi < 60 ? `${pad(h)}:${pad(mi)}` : null;
}

const naarIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const isoNaarDate = (iso) => { const m = ISO.exec(String(iso || "").slice(0, 10)); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined; };
const event = (value, name, id, type) => ({ target: { value, name, id, type }, currentTarget: { value, name, id, type }, preventDefault() {}, stopPropagation() {} });

const VELD = "flex h-9 w-full rounded-md border border-input bg-transparent py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";

function useTekst(value, naarTekst, parse) {
  const [tekst, setTekst] = React.useState(() => naarTekst(value));
  React.useEffect(() => { if ((parse(tekst) || "") !== (value || "")) setTekst(naarTekst(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return [tekst, setTekst];
}

function MaandJaarKop({ maand, setMaand, van, tot }) {
  const jaren = [];
  for (let j = tot; j >= van; j -= 1) jaren.push(j);
  const sel = "h-8 rounded-md border border-input bg-white px-1.5 text-sm";
  return (
    <div className="flex gap-1.5 px-3 pt-3">
      <select className={cn(sel, "flex-1")} value={maand.getMonth()} onChange={(e) => setMaand(new Date(maand.getFullYear(), +e.target.value, 1))} aria-label="Maand">
        {MAANDEN.map((m, i) => <option key={m} value={i}>{m}</option>)}
      </select>
      <select className={sel} value={maand.getFullYear()} onChange={(e) => setMaand(new Date(+e.target.value, maand.getMonth(), 1))} aria-label="Jaar">
        {jaren.map((j) => <option key={j} value={j}>{j}</option>)}
      </select>
    </div>
  );
}

/** Datum (dd-mm-jjjj) met kalender; value/onChange in ISO JJJJ-MM-DD. */
export const DatumInvoer = React.forwardRef(function DatumInvoer({ value, onChange, onBlur, min, max, className, disabled, placeholder, name, id, required, readOnly, ...rest }, ref) {
  const waarde = String(value ?? "").slice(0, 10);
  const [tekst, setTekst] = useTekst(waarde, isoNaarNl, nlNaarIso);
  const [open, setOpen] = React.useState(false);
  const gekozen = isoNaarDate(waarde);
  const [maand, setMaand] = React.useState(gekozen || new Date());
  React.useEffect(() => { if (open) setMaand(gekozen || isoNaarDate(min) || new Date()); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const zend = (v) => { if (v !== waarde) onChange?.(event(v, name, id, "date")); };
  const binnen = (iso) => (!min || iso >= String(min).slice(0, 10)) && (!max || iso <= String(max).slice(0, 10));
  const typ = (e) => {
    const { tekst: t, iso } = maskeer(e.target.value);
    setTekst(t);
    if (!t.trim()) zend("");
    else if (iso && binnen(iso)) zend(iso);
  };
  const verlaat = (e) => {
    const iso = nlNaarIso(tekst);
    if (iso && !binnen(iso)) toast.error(min && iso < String(min).slice(0, 10) ? `Kies een datum vanaf ${isoNaarNl(min)}` : `Kies een datum tot en met ${isoNaarNl(max)}`);
    setTekst(isoNaarNl(iso && binnen(iso) ? iso : waarde));
    onBlur?.(e);
  };
  const isoTekst = nlNaarIso(tekst);
  const ongeldig = !!tekst.trim() && (isoTekst ? !binnen(isoTekst) : tekst.length >= 10);
  const { box, veld, breed } = splitClass(className);
  const jaar = new Date().getFullYear();
  return (
    <div className={cn("relative", breed ? "" : "w-full", box)}>
      <input ref={ref} type="text" inputMode="numeric" autoComplete="off" maxLength={10} placeholder={placeholder || "dd-mm-jjjj"} value={tekst} onChange={typ} onBlur={verlaat}
        disabled={disabled} readOnly={readOnly} name={name} id={id} required={required} aria-invalid={ongeldig || undefined}
        title={ongeldig ? (min || max ? `Datum buiten bereik${min ? ` (vanaf ${isoNaarNl(min)})` : ""}${max ? ` (t/m ${isoNaarNl(max)})` : ""}` : "Ongeldige datum (dd-mm-jjjj)") : undefined}
        className={cn(VELD, "pl-3 pr-8", veld, ongeldig && "border-rose-400 focus-visible:ring-rose-400")} {...rest} />
      {!disabled && !readOnly && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" tabIndex={-1} aria-label="Kalender openen" className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <CalendarDays className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <MaandJaarKop maand={maand} setMaand={setMaand} van={Math.min(1920, gekozen?.getFullYear() || jaar)} tot={Math.max(jaar + 10, gekozen?.getFullYear() || jaar)} />
            <Calendar mode="single" locale={nl} weekStartsOn={1} month={maand} onMonthChange={setMaand} selected={gekozen}
              disabled={[...(min ? [{ before: isoNaarDate(min) }] : []), ...(max ? [{ after: isoNaarDate(max) }] : [])]}
              onSelect={(d) => { if (d) { const iso = naarIso(d); setTekst(isoNaarNl(iso)); zend(iso); } setOpen(false); }} />
            {!required && waarde && <button type="button" className="w-full border-t py-2 text-xs text-slate-500 hover:bg-slate-50" onClick={() => { setTekst(""); zend(""); setOpen(false); }}>Datum wissen</button>}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
});

const TIJDEN = Array.from({ length: 96 }, (_, i) => `${pad(Math.floor(i / 4))}:${pad((i % 4) * 15)}`);

/** Tijd (uu:mm, 24-uurs) met kwartierlijst; value/onChange in UU:MM. */
export const TijdInvoer = React.forwardRef(function TijdInvoer({ value, onChange, onBlur, className, disabled, placeholder, name, id, required, readOnly, step, min, max, ...rest }, ref) {
  const waarde = String(value ?? "").slice(0, 5);
  const [tekst, setTekst] = useTekst(waarde, (v) => v || "", (t) => normTijd(t));
  const [open, setOpen] = React.useState(false);
  const lijst = React.useRef(null);
  const zend = (v) => { if (v !== waarde) onChange?.(event(v, name, id, "time")); };
  const typ = (e) => {
    let t = e.target.value;
    if (/^\d+$/.test(t) && t.length > 2 && t.length <= 4) t = `${t.slice(0, 2)}:${t.slice(2)}`;
    setTekst(t);
    if (!t.trim()) zend("");
    else { const v = /^\d{1,2}:\d{2}$|^\d{1,2}\.\d{2}$/.test(t.trim()) ? normTijd(t) : null; if (v) zend(v); }
  };
  const verlaat = (e) => { const v = normTijd(tekst, true); if (v) { setTekst(v); zend(v); } else if (tekst.trim()) setTekst(waarde); onBlur?.(e); };
  React.useEffect(() => { if (open) setTimeout(() => lijst.current?.querySelector("[data-gekozen='1']")?.scrollIntoView({ block: "center" }), 0); }, [open]);
  const { box, veld, breed } = splitClass(className);
  return (
    <div className={cn("relative", breed ? "" : "w-full", box)}>
      <input ref={ref} type="text" inputMode="numeric" autoComplete="off" maxLength={5} placeholder={placeholder || "uu:mm"} value={tekst} onChange={typ} onBlur={verlaat}
        disabled={disabled} readOnly={readOnly} name={name} id={id} required={required} className={cn(VELD, "pl-3 pr-8", veld)} {...rest} />
      {!disabled && !readOnly && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" tabIndex={-1} aria-label="Tijd kiezen" className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100">
              <Clock className="h-4 w-4" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-28 p-1" align="end">
            <div ref={lijst} className="max-h-60 overflow-y-auto">
              {TIJDEN.filter((t) => (!min || t >= min) && (!max || t <= max)).map((t) => (
                <button key={t} type="button" data-gekozen={t === waarde ? "1" : "0"} onClick={() => { setTekst(t); zend(t); setOpen(false); }}
                  className={cn("block w-full rounded px-2 py-1 text-left text-sm tabular-nums hover:bg-slate-100", t === waarde && "bg-slate-900 text-white hover:bg-slate-800")}>{t}</button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
});

/** Datum + tijd; value/onChange in JJJJ-MM-DDTUU:MM. */
export const DatumTijdInvoer = React.forwardRef(function DatumTijdInvoer({ value, onChange, className, disabled, name, id, min, max, required, readOnly, ...rest }, ref) {
  const [d, t] = String(value ?? "").split("T");
  const [tijd, setTijd] = React.useState((t || "").slice(0, 5));
  React.useEffect(() => { if ((t || "").slice(0, 5) && (t || "").slice(0, 5) !== tijd) setTijd((t || "").slice(0, 5)); }, [t]); // eslint-disable-line react-hooks/exhaustive-deps
  const zend = (dd, tt) => onChange?.(event(dd ? `${dd}T${tt || "00:00"}` : "", name, id, "datetime-local"));
  const { box, veld, breed } = splitClass(className);
  return (
    <div className={cn("flex gap-1.5", breed ? "" : "w-full", box)}>
      <DatumInvoer ref={ref} value={d || ""} onChange={(e) => zend(e.target.value, tijd)} className={cn("flex-1 min-w-[8.5rem]", veld)} disabled={disabled} readOnly={readOnly}
        required={required} min={min ? String(min).slice(0, 10) : undefined} max={max ? String(max).slice(0, 10) : undefined} id={id} {...rest} />
      <TijdInvoer value={tijd} onChange={(e) => { setTijd(e.target.value); if (d) zend(d, e.target.value); }} className={cn("w-[5.5rem] shrink-0", veld)} disabled={disabled} readOnly={readOnly}
        data-testid={rest["data-testid"] ? `${rest["data-testid"]}-tijd` : undefined} />
    </div>
  );
});

/** Maand (januari … december + jaar); value/onChange in JJJJ-MM. */
export const MaandInvoer = React.forwardRef(function MaandInvoer({ value, onChange, className, disabled, name, id, min, max, required, ...rest }, ref) {
  const [j, m] = String(value ?? "").split("-");
  const nu = new Date().getFullYear();
  const van = min ? +String(min).slice(0, 4) : Math.min(nu - 6, +j || nu);
  const tot = max ? +String(max).slice(0, 4) : Math.max(nu + 3, +j || nu);
  const jaren = [];
  for (let x = tot; x >= van; x -= 1) jaren.push(x);
  const zend = (jj, mm) => onChange?.(event(jj && mm ? `${jj}-${mm}` : "", name, id, "month"));
  const { box, veld, breed } = splitClass(className);
  const sel = cn("h-9 rounded-md border border-input bg-transparent px-2 text-base shadow-sm md:text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50", veld);
  return (
    <div className={cn("flex gap-1.5", breed ? "" : "w-full", box)} {...rest}>
      <select ref={ref} className={cn(sel, "flex-1 min-w-0")} value={m || ""} disabled={disabled} required={required} aria-label="Maand" id={id}
        onChange={(e) => zend(j || String(nu), e.target.value)} data-testid={rest["data-testid"] ? `${rest["data-testid"]}-maand` : undefined}>
        {!required && <option value="">maand</option>}
        {MAANDEN.map((naam, i) => <option key={naam} value={pad(i + 1)}>{naam}</option>)}
      </select>
      <select className={cn(sel, "w-[5.5rem]")} value={j || ""} disabled={disabled} required={required} aria-label="Jaar" onChange={(e) => zend(e.target.value, m || "01")}
        data-testid={rest["data-testid"] ? `${rest["data-testid"]}-jaar` : undefined}>
        {!required && <option value="">jaar</option>}
        {jaren.map((x) => <option key={x} value={x}>{x}</option>)}
      </select>
    </div>
  );
});
